import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
const JOBS_PER_PAGE = 12;

const JobsMore = () => {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);
  
  const fetchJobs = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/jobs/all`
      );

      const jobsData = Array.isArray(response.data)
        ? response.data
        : response.data.jobs || [];

      const activeJobs = jobsData.filter((job) => {
        if (job.status && job.status.toLowerCase() !== 'active') {
          return false;
        }

        if (job.expires_at && new Date(job.expires_at) < new Date()) {
          return false;
        }

        return true;
      });

      setJobs(activeJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = useMemo(() => {
    return jobs
      .filter((job) => {
        const searchText = search.toLowerCase();

        return (
          (job.role || '').toLowerCase().includes(searchText) ||
          (job.organization_name || '').toLowerCase().includes(searchText) ||
          (job.company_name || '').toLowerCase().includes(searchText) ||
          (job.location || '').toLowerCase().includes(searchText) ||
          (job.skills_required || '').toLowerCase().includes(searchText) ||
          (job.experience_required || '').toLowerCase().includes(searchText)
        );
      })
      .filter((job) => {
        if (selectedFilter === 'All') return true;

        if (selectedFilter === 'Remote') {
          return (job.location || '').toLowerCase().includes('remote');
        }

        if (selectedFilter === 'Nurse') {
          return (job.role || '').toLowerCase().includes('nurse');
        }

        if (selectedFilter === 'Full Time') {
          return true;
        }

        return true;
      });
  }, [jobs, search, selectedFilter]);

  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);

  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * JOBS_PER_PAGE,
    currentPage * JOBS_PER_PAGE
  );

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-10">
          <button
            onClick={() => navigate('/')}
            className="mb-5 text-sm font-semibold text-blue-600 hover:underline"
          >
            ← Back to Home
          </button>

          <h1 className="text-3xl font-bold text-gray-900">
            All Nursing Jobs
          </h1>

          <p className="mt-2 text-gray-600">
            Explore all currently available healthcare and nursing opportunities.
          </p>
        </div>

        <div className="mb-8 rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row">
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search jobs, hospitals, locations or skills..."
              className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-blue-600"
            />

            <div className="flex flex-wrap gap-2">
              {['All', 'Remote', 'Full Time', 'Nurse'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => handleFilterChange(filter)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${
                    selectedFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-blue-100'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-600">
            Loading jobs...
          </div>
        ) : paginatedJobs.length === 0 ? (
          <div className="rounded-2xl bg-white py-20 text-center shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800">
              No jobs found
            </h2>
            <p className="mt-2 text-gray-500">
              Try changing your search or filter.
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {paginatedJobs.map((job) => (
                <div
                  key={job.id}
                  onClick={() => navigate(`/job/${job.id}`)}
                  className="cursor-pointer rounded-2xl bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <h2 className="text-lg font-bold text-gray-900">
                      {job.role}
                    </h2>

                    <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                      Active
                    </span>
                  </div>

                  <p className="mb-2 text-sm font-semibold text-blue-600">
                    {job.organization_name ||
                      job.company_name ||
                      'Healthcare Institution'}
                  </p>

                  <p className="mb-2 text-sm text-gray-600">
                    Location: {job.location || 'Not specified'}
                  </p>

                  <p className="mb-2 text-sm text-gray-600">
                    Experience: {job.experience_required || 'Not specified'}
                  </p>

                  <p className="mb-5 text-sm text-gray-600">
                    Salary: {job.salary || 'Not specified'}
                  </p>

                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/job/${job.id}`);
                    }}
                    className="w-full rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700"
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => page - 1)}
                  className="rounded-lg border px-4 py-2 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`rounded-lg px-4 py-2 ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border bg-white text-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((page) => page + 1)}
                  className="rounded-lg border px-4 py-2 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JobsMore;